'use client';

import { FC, ReactElement } from 'react';

import classNames from 'classnames';

import { useControlledValue } from '../../../../hooks/useControlledValue/useControlledValue';
import { IconButton } from '../../../button/components/IconButton/IconButton';
import { DotsStepper } from '../../../stepper/components/DotsStepper/DotsStepper';

// TODO: Add swipeable prop

interface Props {
   currentSlide?: number;
   defaultSlide?: number;
   showIndicators?: boolean;
   showControls?: boolean;
   fullWidth?: boolean;
   variant?: 'default' | 'on-primary';
   controlVariant?: 'on-light' | 'on-primary' | 'on-dark' | 'danger';
   controlPlacement?: 'inside' | 'outside' | 'hover-inside';
   backControlIcon?: string;
   backControlAriaLabel?: string;
   nextControlIcon?: string;
   nextControlAriaLabel?: string;
   onSlideChange?: (currentSlide: number) => void;
   children: Array<ReactElement>;
}

export const Carousel: FC<Props> = ({
   currentSlide: currentSlideFromProps,
   defaultSlide,
   showIndicators = false,
   showControls = true,
   controlVariant = 'on-light',
   controlPlacement = 'inside',
   backControlIcon = 'long-arrow-left',
   backControlAriaLabel,
   nextControlIcon = 'long-arrow-right',
   nextControlAriaLabel,
   fullWidth = false,
   onSlideChange,
   variant,
   children
}) => {
   const [currentSlide, setCurrentSlide] = useControlledValue(currentSlideFromProps, onSlideChange, defaultSlide || 0);

   const onNextSlide = () => {
      const newSlide = currentSlide + 1;
      if (newSlide > children.length - 1) {
         setCurrentSlide(0);
      } else {
         setCurrentSlide(newSlide);
      }
   };

   const onPrevSlide = () => {
      const newSlide = currentSlide - 1;
      if (newSlide < 0) {
         setCurrentSlide(children.length - 1);
      } else {
         setCurrentSlide(newSlide);
      }
   };

   return (
      <div data-testid="carousel" className={`gap-component-data-display-carousel-container-spacing-outer-gap flex flex-col items-center ${fullWidth ? 'w-full' : ''}`.replace(/\s+/g, ' ')}>
         <div className="group/carousel gap-component-data-display-carousel-container-spacing-outer-gap relative flex w-full items-center justify-center">
            {showControls && (
               <div
                  className={classNames({
                     'm-component-data-display-carousel-container-spacing-margin absolute left-0 z-10': controlPlacement === 'inside',
                     'absolute -translate-x-1/2 left-0 opacity-0 group-hover/carousel:opacity-100 transition-opacity z-10': controlPlacement === 'hover-inside'
                  })}
               >
                  <IconButton ariaLabel={backControlAriaLabel ?? 'Previous slide'} onClick={onPrevSlide} variant={controlVariant} icon={backControlIcon} backdrop />
               </div>
            )}
            <div className="w-full">
               {children.map((child, index) => {
                  const isActive = currentSlide === index;
                  return (
                     // eslint-disable-next-line @eslint-react/no-array-index-key
                     <div key={index} aria-hidden={!isActive} style={{ display: isActive ? 'block' : 'none' }}>
                        {child}
                     </div>
                  );
               })}
            </div>
            {showControls && (
               <div
                  className={classNames('', {
                     'm-component-data-display-carousel-container-spacing-margin absolute right-0 z-10': controlPlacement === 'inside',
                     'absolute translate-x-1/2 right-0 opacity-0 group-hover/carousel:opacity-100 transition-opacity z-10': controlPlacement === 'hover-inside'
                  })}
               >
                  <IconButton ariaLabel={nextControlAriaLabel ?? 'Next slide'} onClick={onNextSlide} variant={controlVariant} icon={nextControlIcon} backdrop />
               </div>
            )}
         </div>
         {showIndicators && (
            <div data-testid="carousel-indicators">
               <DotsStepper numberOfSteps={children.length} currentStep={currentSlide} onStepChange={(step) => setCurrentSlide(step)} variant={variant} />
            </div>
         )}
      </div>
   );
};
